/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * CheckoutPage - Handles checkout deeplinks with tier and promo code params
 *
 * URL format: /checkout?tier=PRO&promo=20OFF&cycle=monthly
 */

import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { Loader2, Check, Tag, ArrowLeft, AlertCircle, Info } from 'lucide-react'
import toast from 'react-hot-toast'
import subscriptionService from '../services/subscriptionService'
import { createLogger } from '../utils/logger'

const logger = createLogger('CheckoutPage')

const CheckoutPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const tierCode = searchParams.get('tier')?.toUpperCase()
  const promoCode = searchParams.get('promo')?.toUpperCase()
  const billingCycle = searchParams.get('cycle') || 'monthly'

  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [tier, setTier] = useState(null)
  const [discount, setDiscount] = useState(null)
  const [error, setError] = useState(null)
  const [billingReadOnly, setBillingReadOnly] = useState(false)

  useEffect(() => {
    loadCheckoutData()
  }, [tierCode, promoCode])

  const loadCheckoutData = async () => {
    if (!tierCode) {
      setError('No tier specified. Please select a plan.')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      // Check if billing is read-only
      try {
        const flagsRes = await fetch('/api/subscription/feature-flags', { credentials: 'include' })
        if (flagsRes.ok) {
          const flagsData = await flagsRes.json()
          if (flagsData.readonly_billing === true) {
            setBillingReadOnly(true)
            setLoading(false)
            return // Stop loading - billing is disabled
          }
        }
      } catch (flagError) {
        logger.error('Error checking billing flags:', flagError)
      }

      // Load tier info
      const tierResult = await subscriptionService.getTierByCode(tierCode.toLowerCase())
      if (!tierResult.success || !tierResult.tier) {
        setError(`Plan "${tierCode}" not found.`)
        setLoading(false)
        return
      }
      setTier(tierResult.tier)

      // Validate promo code if provided
      if (promoCode) {
        try {
          const promoResult = await subscriptionService.validatePromoCode(promoCode, tierCode)
          if (promoResult.success && promoResult.valid) {
            setDiscount(promoResult.discount)
          } else {
            // Promo code invalid but don't block checkout
            toast.error(`Promo code "${promoCode}" is not valid for this plan`)
          }
        } catch (err) {
          logger.warn('Promo validation failed:', err)
          // Don't block checkout if promo validation fails
        }
      }
    } catch (err) {
      logger.error('Failed to load checkout data:', err)
      setError('Failed to load plan details. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleCheckout = async () => {
    try {
      setProcessing(true)

      const result = await subscriptionService.createCheckoutSession({
        tier: tierCode,
        billingCycle,
        promotionCode: discount ? promoCode : undefined
      })

      if (result.success && result.url) {
        // Redirect to Stripe Checkout
        window.location.href = result.url
      } else {
        throw new Error(result.error || 'Failed to create checkout session')
      }
    } catch (err) {
      logger.error('Checkout error:', err)
      toast.error(err.message || 'Failed to start checkout')
      setProcessing(false)
    }
  }

  const formatPrice = (cents) => {
    return `$${(cents / 100).toFixed(2)}`
  }

  const calculateDiscountedPrice = (originalCents) => {
    if (!discount) return originalCents

    if (discount.percentOff) {
      return originalCents * (1 - discount.percentOff / 100)
    }
    if (discount.amountOffCents) {
      return Math.max(0, originalCents - discount.amountOffCents)
    }
    return originalCents
  }

  const price = tier ? (billingCycle === 'annual' ? tier.annualPrice : tier.monthlyPrice) : 0
  const discountedPrice = calculateDiscountedPrice(price)

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary-500 mx-auto" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading checkout...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Checkout Error
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <Link
            to="/profile#billing"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <ArrowLeft className="w-4 h-4" />
            View Plans
          </Link>
        </div>
      </div>
    )
  }

  // Billing is disabled during beta period
  if (billingReadOnly) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
          <Info className="w-16 h-16 text-primary-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Checkout Unavailable
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Subscriptions are not available during our beta period. All features are currently
            unlocked for free!
          </p>
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <ArrowLeft className="w-4 h-4" />
            Go to Dashboard
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4">
      <div className="max-w-lg mx-auto">
        {/* Back link */}
        <Link
          to="/profile#billing"
          className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to plans
        </Link>

        {/* Checkout Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-6 text-white">
            <h1 className="text-2xl font-bold">Complete Your Upgrade</h1>
            <p className="text-primary-100 mt-1">Subscribe to {tier?.name}</p>
          </div>

          {/* Plan Details */}
          <div className="p-6 space-y-6">
            {/* Tier Info */}
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {tier?.name}
                </h2>
                <p className="text-gray-600 dark:text-gray-400">{tier?.description}</p>
              </div>
              <div className="text-right">
                {discount ? (
                  <>
                    <p className="text-sm text-gray-500 line-through">{formatPrice(price)}</p>
                    <p className="text-2xl font-bold text-primary-600">
                      {formatPrice(discountedPrice)}
                    </p>
                  </>
                ) : (
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {formatPrice(price)}
                  </p>
                )}
                <p className="text-sm text-gray-500">
                  /{billingCycle === 'annual' ? 'year' : 'month'}
                </p>
              </div>
            </div>

            {/* Discount Applied */}
            {discount && (
              <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <Tag className="w-5 h-5 text-green-600 dark:text-green-400" />
                <div className="flex-1">
                  <p className="font-medium text-green-800 dark:text-green-200">
                    {discount.name} applied!
                  </p>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    {discount.percentOff
                      ? `${discount.percentOff}% off`
                      : `$${(discount.amountOffCents / 100).toFixed(2)} off`}
                    {discount.duration === 'once' && ' (first payment)'}
                    {discount.duration === 'forever' && ' (forever)'}
                    {discount.duration === 'repeating' &&
                      ` (for ${discount.durationInMonths} months)`}
                  </p>
                </div>
                <Check className="w-5 h-5 text-green-600" />
              </div>
            )}

            {/* Billing Cycle Toggle */}
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Billing Cycle
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() =>
                    navigate(
                      `/checkout?tier=${tierCode}${promoCode ? `&promo=${promoCode}` : ''}&cycle=monthly`
                    )
                  }
                  className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium ${
                    billingCycle === 'monthly'
                      ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() =>
                    navigate(
                      `/checkout?tier=${tierCode}${promoCode ? `&promo=${promoCode}` : ''}&cycle=annual`
                    )
                  }
                  className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium ${
                    billingCycle === 'annual'
                      ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  Annual (Save 17%)
                </button>
              </div>
            </div>

            {/* Features */}
            {tier?.features && Object.keys(tier.features).length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Includes
                </p>
                <ul className="space-y-2">
                  {Object.entries(tier.features)
                    .filter(([, enabled]) => enabled)
                    .map(([feature]) => (
                      <li
                        key={feature}
                        className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400"
                      >
                        <Check className="w-4 h-4 text-green-500" />
                        {feature.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                      </li>
                    ))}
                </ul>
              </div>
            )}

            {/* Checkout Button */}
            <button
              onClick={handleCheckout}
              disabled={processing}
              className="w-full py-3 px-6 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {processing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Redirecting to checkout...
                </>
              ) : (
                <>Continue to Payment</>
              )}
            </button>

            <p className="text-xs text-center text-gray-500 dark:text-gray-400">
              You will be redirected to Stripe for secure payment processing.
              <br />
              Cancel anytime from your billing settings.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CheckoutPage
